import { useCallback, useEffect, useRef, useState } from "react";
import { useReq } from "@larner.dev/use-req";
import { MySparkRegistration } from "@greenflagdate/shared";
import { apiReq } from "./apiReq";
import { useLastGood } from "./useLastGood";
import { useStoreData } from "./store/store";

/**
 * The viewer's own seat at an event (their check-in code and the venue's
 * arrival details), and whether the check-in sheet is open. Only asked for
 * once the store says they are registered; `registration` is null until then.
 */
export const useMyRegistration = (
  sparkId: string | undefined,
  registered: boolean,
) => {
  const signedIn = !!useStoreData().auth.token;
  const [req, reqState] = useReq<{
    registration: MySparkRegistration | null;
  }>(apiReq);
  // Which event the last request asked about, so a seat loaded for one event
  // is never shown on another one's page.
  const requestedFor = useRef(sparkId);
  const load = useCallback(() => {
    requestedFor.current = sparkId;
    req.get(`/spark/${sparkId}/my-registration`);
  }, [req, sparkId]);
  useEffect(() => {
    if (registered && signedIn) {
      load();
    }
  }, [registered, signedIn, load]);

  const [checkInOpen, setCheckInOpen] = useState(false);
  const openCheckIn = useCallback(() => {
    setCheckInOpen(true);
    // Fresh on every open, so a code a host has just scanned shows as
    // checked in.
    load();
  }, [load]);
  const closeCheckIn = useCallback(() => setCheckInOpen(false), []);

  // Each request blanks `result` until it lands (and leaves it blank if it
  // fails), so the refetch on open would otherwise unmount the very sheet it
  // opens. Hold the last answer instead; a new one replaces it.
  const lastGood = useLastGood(
    reqState.result
      ? { sparkId: requestedFor.current, registration: reqState.result.registration }
      : null,
  );
  const registration =
    lastGood && lastGood.sparkId === sparkId ? lastGood.registration : null;

  return {
    registration: registered ? registration : null,
    checkInOpen,
    openCheckIn,
    closeCheckIn,
  };
};
